import React, { useId } from "react";

/**
 * @param {import('./spotlightTypes').SpotlightRect | null} primaryRect
 */
function TourBackdropPanels({ primaryRect }) {
  if (!primaryRect) {
    return (
      <div
        className="tour-engine__backdrop tour-engine__backdrop--full"
        aria-hidden="true"
      />
    );
  }

  const { top, left, width, height } = primaryRect;
  const bottom = top + height;
  const right = left + width;

  const panelStyle = {
    position: "fixed",
    left: 0,
    right: 0,
  };

  return (
    <>
      <div
        className="tour-engine__blocker"
        style={{ ...panelStyle, top: 0, height: top }}
        aria-hidden="true"
      />
      <div
        className="tour-engine__blocker"
        style={{ ...panelStyle, top: bottom, bottom: 0 }}
        aria-hidden="true"
      />
      <div
        className="tour-engine__blocker"
        style={{ position: "fixed", top, left: 0, width: left, height }}
        aria-hidden="true"
      />
      <div
        className="tour-engine__blocker"
        style={{ position: "fixed", top, left: right, right: 0, height }}
        aria-hidden="true"
      />
    </>
  );
}

function SpotlightRing({ rect, className = "tour-engine__spotlight-ring" }) {
  return (
    <rect
      x={rect.left}
      y={rect.top}
      width={rect.width}
      height={rect.height}
      rx="12"
      ry="12"
      className={className}
    />
  );
}

/**
 * @param {import('./spotlightTypes').SpotlightRect[]} spotlightRects
 */
function TourSpotlightVisual({ spotlightRects }) {
  const maskId = useId();

  if (!spotlightRects.length) {
    return null;
  }

  return (
    <svg
      className="tour-engine__spotlight-svg"
      aria-hidden="true"
      role="presentation"
    >
      <defs>
        <mask id={maskId}>
          <rect width="100%" height="100%" fill="white" />
          {spotlightRects.map((rect, index) => (
            <rect
              key={`hole-${index}`}
              x={rect.left}
              y={rect.top}
              width={rect.width}
              height={rect.height}
              rx="12"
              ry="12"
              fill="black"
              className="tour-engine__spotlight-hole"
            />
          ))}
        </mask>
      </defs>
      <rect
        width="100%"
        height="100%"
        className="tour-engine__backdrop-fill"
        mask={`url(#${maskId})`}
      />
      {spotlightRects.map((rect, index) => (
        <SpotlightRing
          key={`ring-${index}`}
          rect={rect}
          className={
            index === 0
              ? "tour-engine__spotlight-ring"
              : "tour-engine__spotlight-ring tour-engine__spotlight-ring--secondary"
          }
        />
      ))}
    </svg>
  );
}

/**
 * Spotlight overlay: panel blockers leave the primary target hole open for interaction.
 * Additional regions are visual-only cut-outs in the SVG mask.
 *
 * @param {Object} props
 * @param {import('./spotlightTypes').SpotlightRect | null} props.primaryRect
 * @param {import('./spotlightTypes').SpotlightRect[]} [props.secondaryRects]
 */
export function TourSpotlight({ primaryRect, secondaryRects = [] }) {
  const spotlightRects = primaryRect
    ? [primaryRect, ...secondaryRects]
    : secondaryRects;

  return (
    <>
      <TourBackdropPanels primaryRect={primaryRect} />
      <TourSpotlightVisual spotlightRects={spotlightRects} />
    </>
  );
}
